import { useState, useEffect, useRef } from 'react';

/** Duration must match the `duration-300` class on the sidebar container in AppRouter. */
const SIDEBAR_TRANSITION_MS = 300;

/**
 * Returns a "visual" collapsed flag that lags behind the real one when collapsing,
 * so inner content switches to the collapsed layout only after the CSS width
 * transition has finished.  When *expanding*, it switches immediately so the
 * full-width layout is visible as the container widens.
 */
export function useSidebarTransition(isCollapsed: boolean): boolean {
  const [visualCollapsed, setVisualCollapsed] = useState(isCollapsed);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Expanding switches to the full-width layout immediately. Adjust during render
  // (React's recommended alternative to a synchronous setState inside an effect).
  if (!isCollapsed && visualCollapsed) {
    setVisualCollapsed(false);
  }

  useEffect(() => {
    if (timerRef.current != null) clearTimeout(timerRef.current);

    if (isCollapsed) {
      // Collapsing: delay the layout switch until the CSS transition ends
      timerRef.current = setTimeout(() => {
        setVisualCollapsed(true);
      }, SIDEBAR_TRANSITION_MS);
    }

    return () => {
      if (timerRef.current != null) clearTimeout(timerRef.current);
    };
  }, [isCollapsed]);

  return visualCollapsed;
}
